<script setup>
/**
 * SzBotPanel - Bot IA SSE status panel.
 * Replaces editor + bottom buttons when bot mode is active.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  active: { type: Boolean, default: false },
});

const store = useStore();

const sseConnection = ref(null);
const botConvId = ref(null);
const retries = ref(0);
const maxRetries = 20;
const finalizedConvId = ref(null);
const statusType = ref('idle');
const statusText = ref('Aguardando mensagem');
const previewText = ref('');
const typingActive = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const conversationId = computed(() => currentChat.value?.id);

const szToken = computed(() => {
  const meta = document.querySelector('meta[name="sz-token"]');
  return meta?.getAttribute('content') || '';
});

function connectSSE(convId) {
  if (sseConnection.value && botConvId.value === convId) return;
  disconnectSSE();
  botConvId.value = convId;
  retries.value = 0;
  openSSE(convId);
}

function openSSE(convId) {
  if (!szToken.value) return;
  const es = new EventSource(`/api/bot-ia/stream/${convId}?token=${encodeURIComponent(szToken.value)}`);

  es.onmessage = (ev) => {
    retries.value = 0;
    try {
      const d = JSON.parse(ev.data);
      if (d.type === 'bot_ia_status') {
        if (d.status === 'finalized') {
          handleFinalized(convId, d);
        } else if (d.status === 'typing_message' && d.detail) {
          animateTyping(d.detail);
        } else {
          updateStatus(d.status, d.detail);
        }
      }
    } catch {}
  };

  es.onerror = () => {
    disconnectSSE();
    if (retries.value >= maxRetries) return;
    const delay = Math.min(2000 * Math.pow(2, retries.value), 32000);
    retries.value++;
    setTimeout(() => {
      if (botConvId.value === convId) openSSE(convId);
    }, delay);
  };

  sseConnection.value = es;
}

function disconnectSSE() {
  if (sseConnection.value) {
    sseConnection.value.close();
    sseConnection.value = null;
  }
}

const emit = defineEmits(['deactivate']);

function handleFinalized(convId, data) {
  updateStatus('done', data.detail || 'Finalizado');
  finalizedConvId.value = convId;

  store.dispatch('conversationLabels/get', convId).catch(() => {});

  if (data.contact_id) {
    setTimeout(() => {
      store.dispatch('contactNotes/get', { contactId: data.contact_id }).catch(() => {});
    }, 8000);
  }

  disconnectSSE();

  setTimeout(() => {
    statusType.value = 'idle';
    statusText.value = 'Aguardando mensagem';
    previewText.value = '';
    emit('deactivate');
  }, 3000);
}

function updateStatus(type, detail) {
  statusType.value = type;
  const textMap = {
    thinking: detail || 'Analisando mensagem...',
    tool_call: detail || 'Consultando ferramenta...',
    typing: detail || 'Digitando resposta...',
    recording: detail || 'Gravando audio...',
    done: detail || 'Concluido',
    error: detail || 'Erro no processamento',
  };
  statusText.value = textMap[type] || detail || '';
  if (type === 'thinking') {
    previewText.value = '';
  }
}

function animateTyping(text) {
  if (typingActive.value) return;
  typingActive.value = true;
  updateStatus('typing', 'Digitando resposta...');

  previewText.value = '';
  let i = 0;
  const interval = setInterval(() => {
    if (i >= text.length) {
      clearInterval(interval);
      setTimeout(() => {
        typingActive.value = false;
        updateStatus('done', 'Resposta enviada');
        setTimeout(() => {
          previewText.value = '';
          statusType.value = 'idle';
          statusText.value = 'Aguardando mensagem';
        }, 2500);
      }, 800);
      return;
    }
    previewText.value += text[i];
    i++;
  }, 18);
}

watch(() => props.active, (isActive) => {
  if (isActive && conversationId.value) {
    connectSSE(conversationId.value);
  } else {
    disconnectSSE();
    statusType.value = 'idle';
    statusText.value = 'Aguardando mensagem';
    previewText.value = '';
    typingActive.value = false;
  }
});

watch(conversationId, (newId, oldId) => {
  if (newId !== oldId) {
    statusType.value = 'idle';
    statusText.value = 'Aguardando mensagem';
    previewText.value = '';
    typingActive.value = false;
    if (props.active && newId) {
      connectSSE(newId);
    } else {
      disconnectSSE();
    }
  }
});

onMounted(() => {
  if (props.active && conversationId.value) {
    connectSSE(conversationId.value);
  }
});

onUnmounted(() => {
  disconnectSSE();
});
</script>

<template>
  <div v-if="active" class="sz-bot-panel">
    <!-- Preview area - fills the same space as the editor -->
    <div class="sz-bot-preview">
      <template v-if="previewText">
        <span class="sz-typing-text">{{ previewText }}</span>
        <span class="sz-typing-cursor" />
      </template>
    </div>

    <!-- Status badge - sits where bottom buttons were -->
    <div class="sz-bot-bottom">
      <div class="sz-bot-status-badge" :class="[`sz-status-${statusType}`]">
        <svg
          class="sz-status-star"
          :class="{ 'sz-spin-star': statusType === 'idle' || statusType === 'thinking', 'sz-spin': statusType === 'tool_call' }"
          width="12" height="12" viewBox="0 0 12 12" fill="none"
        >
          <path d="M6 0L6.30378 0.851073C7.11043 3.11104 8.88896 4.88957 11.1489 5.69622L12 6L11.1489 6.30378C8.88896 7.11043 7.11043 8.88896 6.30378 11.1489L6 12L5.69622 11.1489C4.88957 8.88896 3.11104 7.11043 0.851073 6.30378L0 6L0.851073 5.69622C3.11104 4.88957 4.88957 3.11104 5.69622 0.851073L6 0Z" fill="currentColor"/>
        </svg>
        <span class="sz-status-label">{{ statusText }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-bot-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Preview area matches editor min-height */
.sz-bot-preview {
  flex: 1;
  min-height: 5.5rem;
  padding: 8px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-body, #374151);
  max-height: 160px;
  overflow-y: auto;
  font-family: Inter, system-ui, sans-serif;
}

.sz-typing-text {
  white-space: pre-wrap;
}

.sz-typing-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #388E3C;
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: szCursorBlink 0.8s infinite;
}

/* Bottom row - replaces emoji/file/audio buttons */
.sz-bot-bottom {
  display: flex;
  align-items: center;
  padding: 6px 12px 10px;
}

/* Status badge pill */
.sz-bot-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 6px;
  height: 24px;
  background: #EEF6F0;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #236D23;
  width: fit-content;
}

.sz-status-star {
  flex-shrink: 0;
  color: #236D23;
}

.sz-status-label {
  white-space: nowrap;
}

/* Status colors */
.sz-status-thinking { color: #1565C0; background: #E3F2FD; }
.sz-status-thinking .sz-status-star { color: #1565C0; }
.sz-status-tool_call { color: #D97706; background: #FEF3C7; }
.sz-status-tool_call .sz-status-star { color: #D97706; }
.sz-status-typing { color: #388E3C; background: #E8F5E9; }
.sz-status-typing .sz-status-star { color: #388E3C; }
.sz-status-done { color: #388E3C; background: #E8F5E9; }
.sz-status-done .sz-status-star { color: #388E3C; }
.sz-status-error { color: #ef4444; background: #FEE2E2; }
.sz-status-error .sz-status-star { color: #ef4444; }

/* Dark mode */
.dark .sz-bot-preview,
.dark .sz-bot-preview .sz-typing-text { color: #E5E7EB; }
.dark .sz-typing-cursor { background: #4ADE80; }
.dark .sz-bot-status-badge { background: #1B3A1F; color: #4ADE80; }
.dark .sz-status-star { color: #4ADE80; }
.dark .sz-status-thinking { background: #1E3A5F; color: #42A5F5; }
.dark .sz-status-thinking .sz-status-star { color: #42A5F5; }
.dark .sz-status-tool_call { background: #422006; color: #FBBF24; }
.dark .sz-status-tool_call .sz-status-star { color: #FBBF24; }
.dark .sz-status-typing { background: #1B3A1F; color: #4ADE80; }
.dark .sz-status-done { background: #1B3A1F; color: #4ADE80; }
.dark .sz-status-error { background: #3B1111; color: #F87171; }

/* Animations */
.sz-spin-star { animation: szSpinStar 1.2s linear infinite; }
.sz-spin { animation: szSpin 1s linear infinite; }

@keyframes szSpinStar { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes szSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes szCursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
</style>
